import { memo, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';
import { Box, Grid, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Col, Flex, Form, Input, Row } from 'antd';

import { useGetLynFormDataMutation } from 'store/features/comptroller/comptrollerApi';
import { contextToQueryNameMap, findLabelByValue, leaveCategoryOptions, leaveTypeOptions } from './Utils/leaveUtils';
import IdentityValueLabel from './Utils/IdentityValueLabel';

// Icons
// import AssignmentIcon from '@mui/icons-material/Assignment';
import { useSelector } from 'react-redux';
import SectionCard from 'components/section/SectionCard';
import CustomDateInput from 'components/inputs/CustomDateInput';

const LeavePolicyDetails = ({ title, policyData }) => {
	const [form] = Form.useForm();
	const [identity, setIdentity] = useState();
	const categoryLabel = findLabelByValue(leaveCategoryOptions, policyData.policyCategory);
	// const leaveTypeLabel = findLabelByValue(leaveTypeOptions, policyData.leaveType);
	const effectiveDate = policyData.effectiveDateTime ? dayjs(policyData.effectiveDateTime) : null;
	const endDate = policyData.endDate ? dayjs(policyData.endDate) : null;

	// const policyDetails = [
	// 	// { label: 'Leave Type', value: leaveTypeLabel, icon: <BeachAccessIcon /> },
	// 	// { label: 'Policy Name', value: policyData.policyName || 'N/A', icon: <AssignmentIcon /> },
	// 	{ label: 'Effective Date', value: effectiveDate, icon: <EventIcon /> },
	// 	{ label: 'End Date', value: endDate, icon: <EventIcon /> },
	// 	// { label: 'Category', value: categoryLabel, icon: <CategoryIcon /> },
	// ];

	const userId = useSelector((state) => state.profile.profileData.userId);

	// The useGetLynFormDataMutation hook will be used to fetch the data.
	// RTK Query automatically caches requests, so multiple rows with the same 'context'
	// will result in only one API call.
	const [getLynFormData] = useGetLynFormDataMutation();

	// Determine the correct API query name from the context using our map.

	useEffect(() => {
		// Only make an API call if we have a valid query name, user, and value.
		const allContext = policyData?.policyTable?.map((item) => item?.identityContext);
		const uniqueContext = [...new Set(allContext)];

		Promise.all(
			uniqueContext.map(async (context) => {
				const queryName = contextToQueryNameMap[context];
				if (queryName) {
					const res = await getLynFormData({ name: queryName, userId }).unwrap();
					return res?.results;
				}
				return null;
			})
		)
			.then((responses) => {
				const allData = responses.filter(Boolean);

				setIdentity(allData.flat());
			})
			.catch((error) => {
				console.error('Error fetching data:', error);
			});
	}, [userId, policyData]);

	return (
		<SectionCard sx={{ mb: 2 }}>
			{title && (
				<Typography
					variant="h4"
					sx={{
						fontWeight: 600,
						fontSize: '18px',
						color: '#1a3b5d',
						marginBottom: '16px',
						padding: '0 0 12px 0',
						borderBottom: '1px solid #e0e6ed',
					}}
				>
					{title}
				</Typography>
			)}
			<Form
				form={form}
				id="policy-create"
				layout="vertical"
				initialValues={{
					effectiveDateTime: effectiveDate,
					endDate: endDate,
				}}
			>
				<Row gutter={[16, 0]}>
					<Col xs={24} sm={24} md={24} xl={24}>
						<CustomDateInput
							form={form}
							label="Effective Date"
							name="effectiveDateTime"
							disabled={true}
							placeholder={endDate ? 'Select Date' : 'N/A'}
						/>
					</Col>
					<Col xs={24} sm={24} md={24} xl={24}>
						<CustomDateInput form={form} label="End Date" name="endDate" disabled={true} placeholder={endDate ? 'Select Date' : 'N/A'} />
					</Col>
				</Row>
			</Form>

			<Typography variant="h5" sx={{ mb: 0.8, cursor: 'default' }}>
				Policy Rules
			</Typography>

			<TableContainer
				sx={{
					mb: 3,
					'& table, & th, & td': {
						border: '1px solid #e0e0e0',
						borderCollapse: 'collapse',
					},
					'& th': {
						py: '5px',
						px: '11px',
					},
					'& td': {
						p: '5px',
					},
					'& td .ant-form-item': {
						marginBottom: '0px',
					},
					'& .condition-msg': {
						py: 3,
					},
					overflow: 'hidden',
				}}
			>
				<Table
					sx={{
						'& .MuiTableBody-root .MuiTableCell-root': {
							p: 0.5,
						},
						'& .ant-form-item': {
							marginBottom: 0,
						},
					}}
				>
					<TableHead>
						<TableRow sx={{ backgroundColor: '#e5e5e5' }}>
							<TableCell align="center">Identity</TableCell>
							<TableCell align="center">Value</TableCell>
							<TableCell align="center">Entitlement</TableCell>
							<TableCell align="center">{`${categoryLabel === 'Regular Leave' ? 'Accrual' : 'Recurrence'}`}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{policyData?.policyTable?.map((row, index) => (
							<TableRow key={index}>
								<TableCell align="center">{capitalize(row.identityContext)}</TableCell>

								<TableCell align="center">
									<IdentityValueLabel identity={identity} value={row.identityValue} />
								</TableCell>

								<TableCell
									align="center"
									sx={{
										verticalAlign: 'top',
										minWidth: '10rem',
									}}
								>
									{row.entitlementDays} Days
								</TableCell>

								<TableCell
									align="center"
									sx={{
										verticalAlign: 'top',
										minWidth: '10rem',
									}}
								>
									{capitalize(row.recurrence)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</SectionCard>
	);
};

export default memo(LeavePolicyDetails);
